import { Fade, Slide, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { PatientWeight } from "src/api/types";
import { StyledSpinner } from "src/shared/components/StyledSpinner";
import WhiteCard from "src/shared/components/WhiteCard";
import { formatHeight } from "src/utils/formatHeight";

interface Props {
  patientVitals: PatientWeight;
}

const CalculateCriteria = ({ patientVitals }: Props) => {
  const [fadeLoader, setFadeLoader] = useState<boolean>(false);
  const [fadeHeader, setFadeHeader] = useState<boolean>(false);
  const [fadeHeight, setFadeHeight] = useState<boolean>(false);
  const [fadeWeight, setFadeWeight] = useState<boolean>(false);
  const { pathname } = useLocation();
  const slideContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const page = "progress-projection.criteria";

  useEffect(() => {
    const loaderTimeout = setTimeout(() => setFadeLoader(true), 200);
    const headerTimeout = setTimeout(() => setFadeHeader(true), 1000);
    const weightTimeout = setTimeout(() => setFadeWeight(true), 2000);
    const heightTimeout = setTimeout(() => setFadeHeight(true), 2800);

    return () => {
      clearTimeout(loaderTimeout);
      clearTimeout(headerTimeout);
      clearTimeout(weightTimeout);
      clearTimeout(heightTimeout);
    };
  }, [pathname]);

  if (!patientVitals) return null;

  const height = formatHeight(patientVitals.height);
  return (
    <WhiteCard
      spacing={4}
      overflow="hidden"
      height="400px"
      sx={{
        borderRadius: "30px",
        background: `
            linear-gradient(
              180deg,
               rgba(255, 255, 255, 0) 0.31%,
               rgba(255, 255, 255, 0.36) 99.69%
            ),
           #00B6B9
          `,
      }}
      alignItems="center"
    >
      <Stack
        alignItems="center"
        gap={2}
        py={3}
        overflow="hidden"
        ref={slideContainerRef}
      >
        <Fade in={fadeLoader}>
          <StyledSpinner />
        </Fade>
        <Fade in={fadeHeader}>
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight={700}
            color="#fff"
          >
            {t(`${page}.header`)}
          </Typography>
        </Fade>
        <Slide
          direction="left"
          in={fadeWeight}
          container={slideContainerRef.current}
        >
          <Typography textAlign="center" variant="h5" color="#fff">
            {t(`${page}.weight`)}:{" "}
            <span style={{ fontWeight: 600 }}>{patientVitals.weight} lbs</span>
          </Typography>
        </Slide>
        <Slide
          in={fadeHeight}
          direction="right"
          container={slideContainerRef.current}
          mountOnEnter
          unmountOnExit
        >
          <Typography textAlign="center" variant="h5" color="#fff">
            {t(`${page}.height`)}:{" "}
            <span style={{ fontWeight: 600 }}>{height}</span>
          </Typography>
        </Slide>
      </Stack>
    </WhiteCard>
  );
};

export default CalculateCriteria;
